/**
 * Turns an extracted order into the rows the output table renders.
 *
 * Labels come from the `(UI: ...)` notes in the field descriptions in the service's
 * `extraction/models.py`, grouped like the UTURN "Maak enkele shipment" form.
 */

import type {
	ExtractedCargo,
	ExtractedContainer,
	ExtractedDocument,
	ExtractedField,
	ExtractedOrder,
	ExtractedRouteStop,
	OrderValidation,
	ResolvedLocation,
	StopType,
	UturnLocationAction,
	UturnRequest,
	ValidationIssue,
} from './api';

export const EM_DASH = '—';

/** Document total split across the containers; same value for every order. */
export type PricePerContainer = {
	quantity: number;
	currencyCode: string;
} | null;

/** Shown in the Confidence/Source columns when the server did not provide either. */
export const NOT_APPLICABLE = 'n/a';

export interface Row {
	label: string;
	/** `field_path` used to join validation issues onto this row; empty when nothing can match. */
	path: string;
	value: unknown;
	/** Null when confidence is genuinely unavailable, which is different from 0.0. */
	confidence: number | null;
	sourceText: string | null;
	/** Why this row is empty, when the reason is structural rather than "not in the document". */
	note: string | null;
	issues: ValidationIssue[];
}

export interface FieldGroup {
	key: string;
	title: string;
	subtitle: string | null;
	rows: Row[];
	/** Issues whose `field_path` points at the group as a whole rather than one field. */
	issues: ValidationIssue[];
	warning: string | null;
}

interface FieldSpec<K extends string> {
	label: string;
	key: K;
}

type ContainerKey = keyof ExtractedContainer;
type CargoKey = keyof ExtractedCargo;
type StopKey =
	| 'location'
	| 'window_from'
	| 'window_to'
	| 'reference'
	| 'remarks';
type OrderKey = 'shipper_reference' | 'quantity';

const CONTAINER_FIELDS: FieldSpec<ContainerKey>[] = [
	{ label: 'Container type', key: 'container_type' },
	{ label: 'ISO type', key: 'iso_type' },
	{ label: 'Container status', key: 'status' },
	{ label: 'Shipping line', key: 'shipping_line' },
	{ label: 'Container number', key: 'container_number' },
	{ label: 'Tare weight', key: 'tare_weight' },
	{ label: 'Seal', key: 'seal_number' },
];

const CARGO_FIELDS: FieldSpec<CargoKey>[] = [
	{ label: 'Gross weight', key: 'gross_weight' },
	{ label: 'UN code', key: 'un_code' },
	{ label: 'Description', key: 'description' },
	{ label: 'Shipment requirements', key: 'requirements' },
	{ label: 'Additional information', key: 'additional_info' },
];

const STOP_FIELDS: FieldSpec<StopKey>[] = [
	{ label: 'Location', key: 'location' },
	{ label: 'From', key: 'window_from' },
	{ label: 'To', key: 'window_to' },
	{ label: 'Reference', key: 'reference' },
	{ label: 'Remarks', key: 'remarks' },
];

const ORDER_FIELDS: FieldSpec<OrderKey>[] = [
	{ label: 'Invoice reference', key: 'shipper_reference' },
	{ label: 'Quantity', key: 'quantity' },
];

const STOP_TITLES: Record<StopType, string> = {
	pickup: 'Pickup',
	intermediate: 'Intermediate',
	drop: 'Drop',
};

/** `DELIVER` → `Deliver`, `PICK-UP` → `Pick-Up`; leaves unknown values readable. */
function formatActionType(actionType: string): string {
	return actionType
		.toLowerCase()
		.replace(/(^|[-_\s])([a-z])/g, (_match, sep, char) => sep + char.toUpperCase());
}

/**
 * Header label for a stop. Prefers the finer `locationActionType` from the index-aligned
 * UTURN action (so `DELIVER` reads "Deliver", not "Drop"), falling back to the coarse
 * `stop_type` when no action is aligned with this stop.
 */
function stopTitle(
	stop: ExtractedRouteStop,
	action: UturnLocationAction | undefined,
): string {
	if (action?.locationActionType) return formatActionType(action.locationActionType);
	return STOP_TITLES[stop.stop_type] ?? stop.stop_type;
}

/** Rendered for a label the UTURN payload simply does not carry. */
const NOT_IN_PAYLOAD = 'Not present in the UTURN payload';

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** Render an extracted value for display. Null, absent and blank all read as an em dash. */
export function formatValue(value: unknown): string {
	if (value === null || value === undefined) return EM_DASH;
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	if (Array.isArray(value)) {
		const parts = value
			.map((item) => String(item))
			.filter((item) => item.trim() !== '');
		return parts.length > 0 ? parts.join(', ') : EM_DASH;
	}
	if (typeof value === 'string') return value.trim() === '' ? EM_DASH : value;
	return String(value);
}

/** True when the row holds no value, whatever the reported confidence says. */
export function isEmptyValue(value: unknown): boolean {
	if (value === null || value === undefined) return true;
	if (Array.isArray(value)) return value.length === 0;
	if (typeof value === 'string') return value.trim() === '';
	return false;
}

export function issuesFor(
	validation: OrderValidation | null,
	path: string,
): ValidationIssue[] {
	if (validation === null || path === '') return [];
	return validation.issues.filter((issue) => issue.field_path === path);
}

// ---------------------------------------------------------------------------
// Rows from the persisted extraction (the full case: value + confidence + source)
// ---------------------------------------------------------------------------

function fromField(
	label: string,
	path: string,
	field: ExtractedField<unknown> | undefined,
	validation: OrderValidation | null,
): Row {
	return {
		label,
		path,
		value: field?.value ?? null,
		confidence: field === undefined ? null : field.confidence,
		sourceText: field?.source_text ?? null,
		note: field === undefined ? 'Not present in the server response' : null,
		issues: issuesFor(validation, path),
	};
}

/**
 * The per-container price is not extracted: the server splits the document total evenly
 * across the containers, so it carries no confidence or source text of its own.
 */
function priceRow(price: PricePerContainer): Row {
	return {
		label: 'Price per container',
		path: '',
		value: price === null ? null : `${price.quantity} ${price.currencyCode}`,
		confidence: null,
		sourceText: null,
		note: 'Document total divided by the number of containers',
		issues: [],
	};
}

function fallbackWarning(
	resolved: ResolvedLocation | undefined,
): string | null {
	if (resolved?.fallback !== true) return null;
	const used =
		resolved.fallback_chosen?.name ?? resolved.fallback_query ?? 'unknown';
	return `Address not found — a placeholder location was used (${used}). This stop must be checked manually.`;
}

export function buildGroupsFromExtraction(
	order: ExtractedOrder,
	validation: OrderValidation | null,
	locations: ResolvedLocation[],
	price: PricePerContainer,
	actions: UturnLocationAction[] = [],
): FieldGroup[] {
	const groups: FieldGroup[] = [];

	groups.push({
		key: 'container',
		title: 'Container',
		subtitle: null,
		rows: CONTAINER_FIELDS.map((spec) =>
			fromField(
				spec.label,
				`container.${spec.key}`,
				order.container?.[spec.key],
				validation,
			),
		),
		issues: [],
		warning: null,
	});

	groups.push({
		key: 'cargo',
		title: 'Cargo',
		subtitle: null,
		rows: CARGO_FIELDS.map((spec) =>
			fromField(
				spec.label,
				`cargo.${spec.key}`,
				order.cargo?.[spec.key],
				validation,
			),
		),
		issues: [],
		warning: null,
	});

	const stops = order.stops ?? [];
	if (stops.length === 0) {
		// `missing_route_stop` is reported on the bare `stops` path; with no stops to hang it
		// on it would otherwise vanish, so the group is still rendered.
		groups.push({
			key: 'route',
			title: 'Route',
			subtitle: 'No route stops found in the document',
			rows: [],
			issues: issuesFor(validation, 'stops'),
			warning: null,
		});
	} else {
		stops.forEach((stop, index) => {
			const resolved = locations[index];
			groups.push({
				key: `route-${index}`,
				title: `Route · ${stopTitle(stop, actions[index])}`,
				subtitle: resolved?.chosen?.name ?? null,
				rows: STOP_FIELDS.map((spec) =>
					fromField(
						spec.label,
						`stops[${index}].${spec.key}`,
						stop[spec.key],
						validation,
					),
				),
				issues: [
					// Document-wide route issues are shown on the first stop rather than dropped.
					...(index === 0 ? issuesFor(validation, 'stops') : []),
					...issuesFor(validation, `stops[${index}]`),
				],
				warning: fallbackWarning(resolved),
			});
		});
	}

	groups.push({
		key: 'order',
		title: 'Order',
		subtitle: null,
		rows: [
			...ORDER_FIELDS.map((spec) =>
				fromField(spec.label, spec.key, order[spec.key], validation),
			),
			priceRow(price),
		],
		issues: [],
		warning: null,
	});

	return groups;
}

// ---------------------------------------------------------------------------
// Rows from the UTURN payload (the degraded case: value only, no confidence)
// ---------------------------------------------------------------------------

function fromPayload(
	label: string,
	path: string,
	value: unknown,
	validation: OrderValidation | null,
	options: { note?: string } = {},
): Row {
	return {
		label,
		path,
		value,
		confidence: null,
		sourceText: null,
		note: options.note ?? null,
		issues: issuesFor(validation, path),
	};
}

/**
 * Build the table from `submissions[i].request` — the camelCase payload pushed to UTURN.
 *
 * Used when the extraction was not persisted (`submission_id` is null). This is lossy:
 * the payload carries the mapped UTURN values, not the reading, so several labels have
 * no counterpart at all. Those rows say so rather than silently reading as empty.
 * Validation issues still join on, because they travel with the submit response.
 */
export function buildGroupsFromPayload(
	request: UturnRequest | null,
	validation: OrderValidation | null,
	locations: ResolvedLocation[],
	price: PricePerContainer,
): FieldGroup[] {
	const groups: FieldGroup[] = [];
	const equipment = request?.equipment;

	groups.push({
		key: 'container',
		title: 'Container',
		subtitle: null,
		rows: [
			fromPayload(
				'Container type',
				'container.container_type',
				null,
				validation,
				{
					note: `${NOT_IN_PAYLOAD} — only the ISO code is stored`,
				},
			),
			fromPayload(
				'ISO type',
				'container.iso_type',
				equipment?.isoType ?? null,
				validation,
			),
			fromPayload('Container status', 'container.status', null, validation, {
				note: NOT_IN_PAYLOAD,
			}),
			fromPayload(
				'Shipping line',
				'container.shipping_line',
				request?.shippingLineId == null
					? null
					: `UTURN id ${request.shippingLineId}`,
				validation,
				{
					note: 'The payload contains a UTURN id, not the name of the shipping line',
				},
			),
			fromPayload(
				'Container number',
				'container.container_number',
				equipment?.containerNumber ?? null,
				validation,
			),
			fromPayload('Tare weight', 'container.tare_weight', null, validation, {
				note: NOT_IN_PAYLOAD,
			}),
			fromPayload(
				'Seal',
				'container.seal_number',
				request?.seal ?? null,
				validation,
			),
		],
		issues: [],
		warning: null,
	});

	const weight = request?.grossWeight;
	groups.push({
		key: 'cargo',
		title: 'Cargo',
		subtitle: null,
		rows: [
			fromPayload(
				'Gross weight',
				'cargo.gross_weight',
				weight ? `${weight.quantity} ${weight.unit}` : null,
				validation,
				{
					note: 'Normalized by the server; not the literal text from the document',
				},
			),
			fromPayload('UN code', 'cargo.un_code', null, validation, {
				note: NOT_IN_PAYLOAD,
			}),
			fromPayload(
				'Description',
				'cargo.description',
				request?.cargoDescription ?? null,
				validation,
			),
			fromPayload(
				'Shipment requirements',
				'cargo.requirements',
				request?.requirementIds?.map((id) => `UTURN id ${id}`) ?? null,
				validation,
				{
					note: 'The payload contains UTURN ids, not the names of the requirements',
				},
			),
			fromPayload(
				'Additional information',
				'cargo.additional_info',
				null,
				validation,
				{
					note: NOT_IN_PAYLOAD,
				},
			),
		],
		issues: [],
		warning: null,
	});

	const actions = request?.locationActions ?? [];
	if (actions.length === 0) {
		groups.push({
			key: 'route',
			title: 'Route',
			subtitle: 'No route stops in the payload',
			rows: [],
			issues: issuesFor(validation, 'stops'),
			warning: null,
		});
	} else {
		actions.forEach((action, index) => {
			const resolved = locations[index];
			groups.push({
				key: `route-${index}`,
				title: `Route · ${action.locationActionType}`,
				subtitle: resolved?.chosen?.name ?? null,
				rows: [
					fromPayload(
						'Location',
						`stops[${index}].location`,
						// The payload holds only a place id; the human address survives on the
						// resolved-locations summary, which is index-aligned with the actions.
						resolved?.address ?? action.location?.googlePlaceId ?? null,
						validation,
					),
					fromPayload(
						'From',
						`stops[${index}].window_from`,
						action.dateFrom,
						validation,
						{
							note: 'Normalized to ISO-8601 by the server',
						},
					),
					fromPayload(
						'To',
						`stops[${index}].window_to`,
						action.dateUntil,
						validation,
						{
							note: 'Normalized to ISO-8601 by the server',
						},
					),
					fromPayload(
						'Reference',
						`stops[${index}].reference`,
						action.reference,
						validation,
					),
					fromPayload(
						'Remarks',
						`stops[${index}].remarks`,
						action.remarks,
						validation,
					),
				],
				issues: [
					...(index === 0 ? issuesFor(validation, 'stops') : []),
					...issuesFor(validation, `stops[${index}]`),
				],
				warning: fallbackWarning(resolved),
			});
		});
	}

	groups.push({
		key: 'order',
		title: 'Order',
		subtitle: null,
		rows: [
			fromPayload(
				'Invoice reference',
				'shipper_reference',
				request?.shipperReference ?? null,
				validation,
			),
			fromPayload(
				'Quantity',
				'quantity',
				request?.numberOfCopiesToBeCreated ?? null,
				validation,
			),
			priceRow(price),
		],
		issues: [],
		warning: null,
	});

	return groups;
}

// ---------------------------------------------------------------------------
// Degraded-mode detection
// ---------------------------------------------------------------------------

function collectFields(doc: ExtractedDocument): ExtractedField<unknown>[] {
	const fields: ExtractedField<unknown>[] = [];
	const push = (field: ExtractedField<unknown> | undefined): void => {
		if (field !== undefined) fields.push(field);
	};

	push(doc.shipper);
	push(doc.total_price);

	for (const order of doc.orders ?? []) {
		for (const spec of CONTAINER_FIELDS) push(order.container?.[spec.key]);
		for (const spec of CARGO_FIELDS) push(order.cargo?.[spec.key]);
		for (const stop of order.stops ?? []) {
			for (const spec of STOP_FIELDS) push(stop[spec.key]);
		}
		for (const spec of ORDER_FIELDS) push(order[spec.key]);
	}

	return fields;
}

/**
 * Detect a server running with `CONFIDENCE_SOURCETEXT=false`.
 *
 * That flag does not change the JSON shape: the lean path re-inflates every field to
 * `confidence: 1.0, source_text: null` unconditionally. `/health/config` does not report
 * the flag, so it has to be inferred. The inference is safe because normal extraction
 * guarantees `value === null` implies `confidence === 0.0` — a document in which every
 * single field reports a flat 1.0 with no snippet cannot come from the normal path.
 */
export function isLeanMode(doc: ExtractedDocument): boolean {
	const fields = collectFields(doc);
	if (fields.length === 0) return false;
	return fields.every(
		(field) => field.confidence === 1 && field.source_text === null,
	);
}
